import { BuildStack, BuildStep } from '../stores/useBuildStacksStore.js';

export interface ValidationResult {
  isValid: boolean;
  buildStack?: BuildStack;
  errors: string[];
}

/**
 * Validates that a BuildStep object has the required fields
 */
const validateBuildStep = (step: unknown, index: number): string[] => {
  const errors: string[] = [];
  
  if (!step || typeof step !== 'object') {
    errors.push(`Step ${index + 1}: Must be an object`);
    return errors;
  }
  
  const stepObj = step as Record<string, unknown>;
  
  if (!stepObj.beeName || typeof stepObj.beeName !== 'string') {
    errors.push(`Step ${index + 1}: Missing or invalid 'beeName' field`);
  }
  
  if (!stepObj.command || typeof stepObj.command !== 'string') {
    errors.push(`Step ${index + 1}: Missing or invalid 'command' field`);
  }
  
  if (stepObj.inputs !== undefined && (typeof stepObj.inputs !== 'object' || stepObj.inputs === null)) {
    errors.push(`Step ${index + 1}: 'inputs' must be an object if provided`);
  }
  
  if (stepObj.onError !== undefined && !['halt', 'continue'].includes(stepObj.onError as string)) {
    errors.push(`Step ${index + 1}: 'onError' must be 'halt' or 'continue' if provided`);
  }
  
  if (stepObj.outputsVar !== undefined && typeof stepObj.outputsVar !== 'string') {
    errors.push(`Step ${index + 1}: 'outputsVar' must be a string if provided`);
  }
  
  return errors;
};

/**
 * Validates that a JSON object matches the BuildStack interface
 * Returns a ValidationResult with the validated BuildStack or errors
 */
export const validateBuildStackJSON = (json: unknown): ValidationResult => {
  const errors: string[] = [];
  
  try {
    if (!json || typeof json !== 'object') {
      return {
        isValid: false,
        errors: ['Input must be a valid JSON object']
      };
    }
    
    const jsonObj = json as Record<string, unknown>;
    
    if (!jsonObj.name || typeof jsonObj.name !== 'string' || (jsonObj.name as string).trim() === '') {
      errors.push("Missing or invalid 'name' field - must be a non-empty string");
    }
    
    if (!Array.isArray(jsonObj.steps)) {
      errors.push("Missing or invalid 'steps' field - must be an array");
    } else if (jsonObj.steps.length === 0) {
      errors.push("'steps' array cannot be empty - at least one step is required");
    } else {
      jsonObj.steps.forEach((step: unknown, index: number) => {
        const stepErrors = validateBuildStep(step, index);
        errors.push(...stepErrors);
      });
    }
    
    if (jsonObj.description !== undefined && typeof jsonObj.description !== 'string') {
      errors.push("'description' must be a string if provided");
    }
    
    if (jsonObj.tags !== undefined && !Array.isArray(jsonObj.tags)) {
      errors.push("'tags' must be an array if provided");
    } else if (jsonObj.tags && !(jsonObj.tags as unknown[]).every((tag: unknown) => typeof tag === 'string')) {
      errors.push("All 'tags' must be strings");
    }
    
    if (jsonObj.icon !== undefined && typeof jsonObj.icon !== 'string') {
      errors.push("'icon' must be a string if provided");
    }
    
    if (jsonObj.category !== undefined && !['pre-made', 'client', 'community'].includes(jsonObj.category as string)) {
      errors.push("'category' must be 'pre-made', 'client', or 'community' if provided");
    }
    
    if (jsonObj.id !== undefined && typeof jsonObj.id !== 'string') {
      errors.push("'id' must be a string if provided");
    }
    
    if (jsonObj.createdAt !== undefined && typeof jsonObj.createdAt !== 'string') {
      errors.push("'createdAt' must be a string if provided");
    }
    
    if (jsonObj.updatedAt !== undefined && typeof jsonObj.updatedAt !== 'string') {
      errors.push("'updatedAt' must be a string if provided");
    }
    
    if (errors.length > 0) {
      return {
        isValid: false,
        errors
      };
    }
    
    const now = new Date().toISOString();
    const buildStack: BuildStack = {
      id: (jsonObj.id as string) || `stack_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: (jsonObj.name as string).trim(),
      description: (jsonObj.description as string)?.trim() || '',
      steps: (jsonObj.steps as Record<string, unknown>[]).map((step: Record<string, unknown>): BuildStep => ({
        beeName: step.beeName as string,
        command: step.command as string,
        inputs: (step.inputs as Record<string, unknown>) || {},
        onError: (step.onError as 'halt' | 'continue') || 'halt',
        outputsVar: step.outputsVar as string
      })),
      createdAt: (jsonObj.createdAt as string) || now,
      updatedAt: (jsonObj.updatedAt as string) || now,
      tags: (jsonObj.tags as string[]) || [],
      icon: jsonObj.icon as string,
      category: 'client' // Always set category to 'client' for uploaded stacks
    };
    
    return {
      isValid: true,
      buildStack,
      errors: []
    };
    
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown validation error';
    return {
      isValid: false,
      errors: [`Validation failed: ${errorMessage}`]
    };
  }
};

/**
 * Validates multiple BuildStack JSON objects
 * Returns an array of ValidationResults
 */
export const validateMultipleBuildStackJSONs = (jsonArray: unknown[]): ValidationResult[] => {
  if (!Array.isArray(jsonArray)) {
    return [{
      isValid: false,
      errors: ['Input must be an array of JSON objects']
    }];
  }
  
  return jsonArray.map((json, index) => {
    const result = validateBuildStackJSON(json);
    if (!result.isValid) {
      result.errors = result.errors.map(error => `File ${index + 1}: ${error}`);
    }
    return result;
  });
};

/**
 * Checks if a JSON string can be parsed and validated as a BuildStack
 */
export const validateBuildStackJSONString = (jsonString: string): ValidationResult => {
  try {
    const parsed = JSON.parse(jsonString);
    return validateBuildStackJSON(parsed);
  } catch {
    return {
      isValid: false,
      errors: ['Invalid JSON format - unable to parse']
    };
  }
};

/**
 * Generates a sample BuildStack JSON for reference
 */
export const generateSampleBuildStackJSON = (): BuildStack => {
  const now = new Date().toISOString();
  return {
    id: `sample_${Date.now()}`,
    name: 'Sample BuildStack',
    description: 'A sample BuildStack for reference',
    steps: [
      {
        beeName: 'fileManager',
        command: 'create',
        inputs: {
          path: '/example/file.txt',
          content: 'Hello World'
        },
        onError: 'halt'
      },
      {
        beeName: 'logger',
        command: 'info',
        inputs: {
          message: 'File created successfully'
        },
        onError: 'continue'
      }
    ],
    createdAt: now,
    updatedAt: now,
    tags: ['sample', 'example'],
    icon: '📝',
    category: 'client'
  };
};
